import React, { useState } from "react";
import { authHeaders } from "../utils/authActions";
import { collectSessionDataForErrorReport } from "../utils/helpers";
import axios from "axios";

function MainAppCrash({ originalError }) {
  const [reportSuccess, setReportSuccess] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const [includeSessionData, setIncludeSessionData] = useState(false);
  const [userDescription, setUserDescription] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Check if this is a quota-related error
  const isQuotaError = (error) => {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString() || '';
    
    return (
      errorMessage.toLowerCase().includes('quota') ||
      errorMessage.toLowerCase().includes('exceeded') ||
      errorMessage.toLowerCase().includes('quota') ||
      errorMessage.toLowerCase().includes('storage') ||
      errorMessage.toLowerCase().includes('memory') ||
      errorMessage.toLowerCase().includes('disk') ||
      errorMessage.toLowerCase().includes('space')
    );
  };

  // Helper function to format date
  const formatDate = () => {
    try {
      const date = new Date();
      const pad = (n) => (n < 10 ? "0" + n : n);

      const day = pad(date.getDate());
      const month = pad(date.getMonth() + 1); // Months are zero-based
      const year = date.getFullYear();
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (_) {
      return "unretrieved";
    }
  };

  // Helper function to serialize error
  function serializeError(error) {
    try {
      if (error instanceof Error) {
        return {
          name: error.name,
          message: error.message,
          stack: error.stack,
          ...error,
        };
      }
      // if it's not an Error instance, just return it as is
      return error;
    } catch (e) {
      console.error("Serialization failed: ", e);
      return "Error serialization failed";
    }
  }

  // Function to send the error report
  const handleErrorReport = () => {
    setIsSending(true); // Start loading state
    
    try {
      let baseUrl = "";
      if (window.sessionStorage.getItem("url") !== null) {
        baseUrl = JSON.parse(window.sessionStorage.getItem("url"));
      } else if (window.location.hostname === "app.speqqle.com") {
        baseUrl = "https://beprod1.speqqle.com";
      } else if (window.location.hostname === "test.speqqle.com") {
        baseUrl = "https://betest.speqqle.com";
      } else {
        baseUrl = "https://belab.speqqle.com";
      }

      const authTokenSession = JSON.parse(
        window.sessionStorage.getItem("authToken")
      );

      const sessionErrorLog = window.sessionStorage.getItem("errorLog");
      const currentUserSession = window.sessionStorage.getItem("currentUser");

      let currentUserID = "unretrieved";

      if (currentUserSession !== null) {
        try {
          const parsedUser = JSON.parse(currentUserSession);
          if (Object.prototype.hasOwnProperty.call(parsedUser, "id")) {
            currentUserID = parsedUser.id;
          }
        } catch (_) {
          console.log("Current user parsing error.");
        }
      }

      // Only proceed if we have errorLog & authToken
      if (sessionErrorLog !== null && authTokenSession !== null) {
        const currentUrl = window.location.href;
        let constructedEmail = `<h3>ERROR REPORT FROM:</h3>
        <p><strong>USER ID:</strong> ${currentUserID}</p>
        <p><strong>TIME:</strong> ${formatDate()}</p>
        <p><strong>ORIGIN:</strong> ${currentUrl}</p>
        <p><strong>USER DESCRIPTION:</strong> ${userDescription || 'No description provided'}</p>
        <p><strong>ERROR THAT CAUSED CRASH PAGE:</strong></p>
        <pre>${JSON.stringify(serializeError(originalError), null, 2)}</pre>
        <p><strong>ERROR LOGS:</strong></p>
        <pre>${sessionErrorLog}</pre>`;

        // Use the helper function to collect session data
        const sessionDataHtml = collectSessionDataForErrorReport(includeSessionData);
        if (sessionDataHtml) {
          constructedEmail += sessionDataHtml;
        }

        const messagePayload = {
          email_title: "FE ERROR REPORT",
          email_content: constructedEmail,
        };

        const sendErrorMessage = async (baseUrl, token, data) => {
          return axios.post(
            `${baseUrl}/api/v1/users/contact_admin/`,
            data,
            authHeaders(token)
          );
        };

        // First attempt: try to send with session data if requested
        sendErrorMessage(baseUrl, authTokenSession, messagePayload)
          .then(() => {
            setReportSuccess(true);
            setReportSent(true);
            setIsSending(false); // End loading state
          })
          .catch(() => {            
            // If session data was included and it failed, retry without session data
            if (includeSessionData) {
              // Remove session data and add "session too large" message
              const retryEmail = constructedEmail.replace(sessionDataHtml, '<h4>--- SESSION DATA: Session too large, could not be included ---</h4>');
              
              const retryPayload = {
                email_title: "FE ERROR REPORT",
                email_content: retryEmail,
              };
              
              // Second attempt: send without session data
              sendErrorMessage(baseUrl, authTokenSession, retryPayload)
                .then(() => {
                  setReportSuccess(true);
                  setReportSent(true);
                  setIsSending(false); // End loading state
                })
                .catch((error) => {
                  console.log("Automatic reporting failed.");
                  console.log(error);
                  
                  setReportSuccess(false);
                  setReportSent(true);
                  setIsSending(false); // End loading state
                });
            } else {
              // No session data was requested, so this is a genuine failure
              setReportSuccess(false);
              setReportSent(true);
              setIsSending(false); // End loading state
            }
          });
      } else {
        // If we don't have the needed session data or token, fail gracefully
        setReportSuccess(false);
        setReportSent(true);
        setIsSending(false); // End loading state
      }
    } catch (_) {
      // If something else breaks, also set report as sent but failed
      setReportSuccess(false);
      setReportSent(true);
      setIsSending(false); // End loading state
    }
  };

  // If it's a quota error, show specific guidance
  if (originalError && isQuotaError(originalError)) {
    return (
      <div
        style={{
          width: "95vw",
          height: "95vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "1.5em",
          backgroundColor: "white",
          color: "black",
        }}
      >
        <div style={{ padding: "10vh 10vw", textWrap: "wrap" }}>
          <div style={{ fontWeight: "bold", color: "#3b4b95", marginBottom: "20px" }}>
            Storage Limit Reached
          </div>
          
          The application has exceeded its storage limits. This usually happens when the browser is running out of memory:
          <ul style={{ margin: "20px 0", paddingLeft: "20px" }}>
            <li>Too many data files are loaded</li>
            <li>Too many curves are displayed in graphs</li>
            <li>Other applications are using too much memory</li>
          </ul>
          
          <div style={{ fontWeight: "bold", marginTop: "20px" }}>
            How to fix this:
          </div>
          <ol style={{ margin: "20px 0", paddingLeft: "20px" }}>
            <li>Refresh the page</li>
            <li>Try removing some datafiles from the data tab, or to display less curves in the graphs.</li>
            <li>Close other browser tabs and applications to free up memory</li>
          </ol>
        </div>
      </div>
    );
  }

  // For non-quota errors, show error reporting options like CrashPage
  return (
    <div
      style={{
        width: "95vw",
        height: "95vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "1.5em",
        backgroundColor: "white",
        color: "black",
      }}
    >
      <div style={{ padding: "10vh 10vw", textWrap: "wrap" }}>
        Something went wrong, please try refreshing the page or closing and opening the page again.
        <br />
        <br />
        
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="userDescription"
              style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}
            >
              What action caused the crash? Any context you can provide will help us understand the issue.
            </label>
            <textarea
              id="userDescription"
              value={userDescription}
              onChange={(e) => setUserDescription(e.target.value)}
              placeholder="Please describe the last actions you took before the crash occurred."
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "0.9em",
                resize: "vertical"
              }}
            />
            <div
              style={{
                fontSize: "0.7em",
                marginTop: "5px",
                color: "#555",
                maxWidth: "500px",
              }}
            >
              This information helps us understand what led to the crash and fix it faster.
            </div>
          </div>
          
          <div style={{ marginBottom: "15px" }}>
            <input
              type="checkbox"
              id="includeSessionData"
              checked={includeSessionData}
              onChange={(e) => setIncludeSessionData(e.target.checked)}
            />
            <label
              htmlFor="includeSessionData"
              style={{ marginLeft: "6px", cursor: "pointer" }}
            >
              <strong>Send session data with the report</strong>
            </label>
            <div
              style={{
                fontSize: "0.7em",
                marginTop: "5px",
                color: "#555",
                maxWidth: "500px",
              }}
            >
              Attaching your session data will send your models, files, and all
              other relevant information with this report. This greatly helps us
              identify and fix issues faster.
              <br />
              <em>Note: Very large session data may be automatically truncated to ensure the report can be sent successfully.</em>
            </div>
          </div>
        </div>
        
        <div>You can do an easy automatic report by clicking this button:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px', marginBottom: '10px' }}>
          <button
            style={{
              fontSize: "0.8em",
              padding: "8px 10px",
              backgroundColor: "#3b4b95",
              color: "white",
              borderRadius: "10px",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.6 : 1,
            }}
            onClick={handleErrorReport}
            disabled={isSending}
          >
            Send Report
          </button>
          {isSending && (
            <span style={{ fontSize: "0.8em", color: "#666" }}>
              Sending report...
            </span>
          )}
        </div>
        <br />
        <br />
        {reportSent && !reportSuccess ? (
          <>
            Automatic reporting failed. If you can, please report this to
            support manually.
            <br />
            Support email: contact@speqqle.com
          </>
        ) : null}
        {reportSent && reportSuccess ? (
          <div
            style={{
              color: "#139429",
            }}
          >
            Automatic report sent successfully!
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default MainAppCrash;
